// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  isArray, toString, head, tail, invoke, map, mapValues, isUndefined, curry, concat, escapeRegExp, toNumber,
  isEmpty, find, isNil, assign, flow, pick, forEach, without, isEqual, flatMap, uniqWith, get, getOr, includes,
  inRange, nth, has, filter, first, isBoolean, remove, split, last, endsWith, startsWith, invokeArgs, isString,
  omit, each, findIndex, some, indexOfFrom, reduce, isNaN, isObject, compact, values, defaults, clone, isNumber
} from 'lodash/fp';

!(function () {
  var React = require('react');
  var m = require('mobx');
  var LazyLoadingBar = require('share/components/controls/LazyLoading/Izenda.LazyLoadingBar');
  var convertToDateTimeFormat = require('share/utils/Izenda.ConvertToDateTimeFormat');
  var convertToNumeralFormat = require('share/utils/Izenda.ConvertToNumeralFormat');
  var routeUtils = require('share/utils/Izenda.RouteUtils');
  var ReportPartElement = require('logic/models/Izenda.ReportPartElement');
  var ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
  var ReportPartGridConstants = require('logic/data/Izenda.ReportPartGridConstants');
  var ReportPartChartConstants = require('logic/data/Izenda.ReportPartChartConstants');
  var ConditionalFormattingConstants = require('logic/data/Izenda.ConditionalFormattingConstants');
  var commonDataUtils = require('logic/Izenda.ReportDesignerCommonDataUtils');
  var reportPartElementUtils = require('share/utils/Izenda.ReportPartElementUtils');
  var uiState = require('logic/Izenda.UiState');

  var fetcher = require('fetchers/Izenda.ReportsDataFetcher');
  var validateExistingReport = fetcher.validateExistingReport;
  var validateExistingField = fetcher.validateExistingField;
  var cacheUtils = require('share/utils/Izenda.CacheUtils');

  var moment = require('moment');
  var ReportBodyConstants = require('logic/data/Izenda.ReportBodyConstants');
  var MESSAGE = ReportBodyConstants.MESSAGES;
  var ReportPartFieldConstants = require('logic/data/Izenda.ReportPartFieldConstants');
  var DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE = ReportPartFieldConstants.DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE;
  var DEFAULT_FORMAT_BY_DATA_TYPE = ReportPartFieldConstants.DEFAULT_FORMAT_BY_DATA_TYPE;
  var FIELD_FUNCTION_MODE = ReportPartFieldConstants.FIELD_FUNCTION_MODE;
  var FUNCTION = ReportPartFieldConstants.FUNCTION;
  var FUNCTION_GROUP_NAME = ReportPartFieldConstants.FUNCTION.GROUP.NAME;
  var TimeFormatConstants = require('logic/data/Izenda.TimeFormatConstants');
  var NumericAndMoneyFormatConstants = require('logic/data/Izenda.NumericAndMoneyFormatConstants');
  var DATE_TIME_FORMAT = TimeFormatConstants.DATE_TIME_FORMAT;
  var DATE_TIME_GROUP_BY = TimeFormatConstants.GROUP_BY;
  var FORMATS_NOT_AVAILABLE_FOR_RUNNING_FIELD = NumericAndMoneyFormatConstants.FORMATS_NOT_AVAILABLE_FOR_RUNNING_FIELD;
  var FORMATS_NOT_AVAILABLE_FOR_PART_TYPES = NumericAndMoneyFormatConstants.FORMATS_NOT_AVAILABLE_FOR_PART_TYPES;
  var FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER =
    NumericAndMoneyFormatConstants.FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER;
  var FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL =
    NumericAndMoneyFormatConstants.FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL;
  var DATA_TYPES = ReportDesignerConstants.DATA_TYPES;
  var REPORT_PART_TYPE = ReportDesignerConstants.REPORT_PART_TYPE;
  var GRID_STYLES = ReportPartGridConstants.GRID_STYLES;
  var GRID_CONTAINER_NAME = ReportPartGridConstants.CONTAINER_NAME;
  var AppGlobalConstants = require('logic/data/Izenda.AppGlobalConstants');
  var TIME_FORMAT = AppGlobalConstants.TIME_FORMAT;
  var EMPTY_GUID = require('logic/data/Izenda.ReportsConstants').EMPTY_GUID;
  var MESSAGES = require('logic/data/Izenda.ReportsConstants').MESSAGES;
  var GridDefaultConfig = require('logic/data/Izenda.GridDefaultConfig');
  var ChartDefaultConfig = require('logic/data/Izenda.ChartDefaultConfig');
  var FormDefaultConfig = require('logic/data/Izenda.FormDefaultConfig');
  var AppGlobal = require('Izenda.AppGlobal');
  var config = require('Izenda.Config');
  var DrillPopup = require('modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DrillPopup');
  var MapConstants = require('logic/data/Izenda.ReportPartMapConstants');
  var ThresholdConstants = require('logic/data/Izenda.SettingsByRangeConstants');
  var UIConstants = require('logic/data/Izenda.UIConstants');

  var COLUMN_NAME_PREFIX = ReportDesignerConstants.COLUMN_NAME_PREFIX;

  var cacheOption = {
    persistent: true,
    resolver: function () {
      return JSON.stringify(arguments);
    }
  };

  var getFunctionData = cacheUtils.memoize(commonDataUtils.getFunctionData, cacheOption);
  var getFormatData = cacheUtils.memoize(commonDataUtils.getFormatData, cacheOption);
  var getFunction = cacheUtils.memoize(commonDataUtils.getFunction, cacheOption);

  function buildDataSourceElement(reportPart, element) {
    return {
      columnName: flow(
        find({
          fieldNameAlias: element.field.fieldNameAlias
        }),
        get('columnName')
      )(reportPart.previewDataFieldsMapping),
      reportPartElm: element
    };
  }

  /**
   * get unique name for element
   * @method getUniqueName
   */
  function getUniqueName(reportPartContent, element, originalName) {
    var elements = flow(
      flatMap(
        flow(
          get('elements'),
          invoke('peek')
        )
      ),
      without([element])
    )(reportPartContent.containerCollection);

    return String.generateNewName(originalName, elements, get('name'), '()');
  }

  /**
   * get new name after apply new function
   * @method getNewElementName
   */
  function getNewElementName(element, functionInfo) {

    var functionName = getOr('', 'name', functionInfo);
    var originalFunctionName = getOr('', 'properties.dataFormattings.functionInfo.name', element);
    var regexString = new RegExp(
      String.format(ReportBodyConstants.FIELD_NAME_ALIAS_REGEX, escapeRegExp(originalFunctionName))
    );
    var originalFieldNameAlias = element.name;

    if (regexString.test(originalFieldNameAlias)) {
      var matchGroup = regexString.exec(originalFieldNameAlias);

      originalFieldNameAlias = matchGroup[2] || matchGroup[3];
    }

    return !isEmpty(functionName) && functionName.indexOf('[') < 0 && functionName !== FUNCTION.GROUP.NAME
      ? String.format(ReportBodyConstants.FIELD_NAME_WITH_FUNCTION_TEMPLATE, functionName, originalFieldNameAlias)
      : originalFieldNameAlias;
  }

  function buildFilterDataSubReport(records, subReportMappingFields, previewDataFieldMapping) {
    var result = { success: true, filters: [] };
    var data = flow(
      m.toJS,
      map(mapValues(toString))
    )(records);

    result.filters = map(function (mappingField) {
      var selectedFieldMapping = find(function (fieldMapping) {
        return fieldMapping.fieldNameAlias === mappingField.fieldNameAlias;
      }, previewDataFieldMapping);

      if (!selectedFieldMapping) {
        result.success = false;
      }
      return {
        key: mappingField.fieldNameInSubReport,
        fieldNameAlias: mappingField.fieldNameAlias,
        columnName: get('columnName', selectedFieldMapping),
        linkReportFilter: true
      };
    }, subReportMappingFields);
    if (!result.success) {
      return result;
    }
    var listColumnName = map('columnName', result.filters);

    data = flow(
      map(pick(listColumnName)),
      uniqWith(isEqual)
    )(data);

    if (data.length !== 1) {
      result.success = false;
      return result;
    }

    data = head(data);

    result.filters = map(function (filterItem) {
      const value = get(filterItem.columnName, data);

      return assign(filterItem, { value: value });
    }, result.filters);

    return result;
  }

  /**
   * set function for element
   * @method setFunctionElement
   */
  function setFunctionElement(reportPartContent, element, functionInfo, manualChange, keepOriginalName) {
    var newName = getNewElementName(element, functionInfo);
    var uniqueNewName = getUniqueName(reportPartContent, element, newName);

    !keepOriginalName && element.setName(uniqueNewName);
    element.properties.setFunction(functionInfo);

    return Promise.resolve(manualChange && invokeArgs('afterUpdateFunction', [element], reportPartContent));
  }

  /**
   * get default function for element
   * @method getDefaultFunctionFormat
   */
  function getDefaultFunctionFormat(reportPartContent, container, element, preloadPromises) {
    const dataType = element.fieldDataType;
    if (!reportPartContent.getDefaultFunctionFormat || element.field.hasAggregatedFunction) {
      return getFormatData(dataType, getOr('', dataType, DEFAULT_FORMAT_BY_DATA_TYPE)).then(function (formatData) {
        return {
          functionData: {},
          formatData: formatData || {},
          defaultFunctionConfig: {}
        };
      });
    }

    const defaultData = reportPartContent.getDefaultFunctionFormat(container, dataType) || {
      FORMAT: { NAME: getOr('', dataType, DEFAULT_FORMAT_BY_DATA_TYPE) }
    };

    // load function promise
    let promiseFunction;
    if (preloadPromises && preloadPromises.promiseFunctions) {
      promiseFunction = preloadPromises.promiseFunctions.then(result => {
        // result is [{dataType: ..., querySourceId: ..., data: [...]}]
        const foundResult = find({
          dataType: dataType,
          querySourceId: element.field.querySourceId
        }, result);
        const data = foundResult && foundResult.data;
        return flow(
          find({ name: getOr('', 'FUNCTION.NAME', defaultData) }),
          defaults({})
        )(data);
      });
    } else {
      promiseFunction = getFunctionData(
        FIELD_FUNCTION_MODE.FieldProperty,
        dataType,
        getOr('', 'FUNCTION.NAME', defaultData),
        [element.field.querySourceId]
      );
    }

    // load format promise
    let promiseFormat;
    if (preloadPromises && preloadPromises.promiseFormats) {
      promiseFormat = preloadPromises.promiseFormats.then(result => {
        // result is [{dataType: ..., data: [...]}]
        const foundResult = find({
          dataType: dataType
        }, result);
        const data = foundResult && foundResult.data;
        return flow(
          find({ name: getOr('', 'FORMAT.NAME', defaultData) }),
          defaults({})
        )(data);
      });
    } else {
      promiseFormat = getFormatData(dataType, getOr('', 'FORMAT.NAME', defaultData));
    }

    return Promise.join(promiseFunction, promiseFormat, function (functionData, formatData) {
      return { functionData: functionData, formatData: formatData, defaultFunctionConfig: defaultData };
    });
  }

  function getDefaultFormat(configField, functionInfo) {
    functionInfo = functionInfo || configField.getValueByKey('functionInfo') || {};
    var dataType = functionInfo.formatDataType || configField.getValueByKey('dataFieldType');
    var functionName = getOr('', 'name', functionInfo);
    var defaultFormatName =
      get([dataType, functionName].join('.'), DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE) ||
      get(dataType, DEFAULT_FORMAT_BY_DATA_TYPE);

    if (defaultFormatName) {
      return getFormatData(dataType, defaultFormatName);
    }
    return Promise.resolve();
  }

  /**
   * set default function for element
   * @method setDefaultFunctionElement
   */
  function setDefaultFunctionElement(reportPartContent, container, element, preloadPromises) {
    return getDefaultFunctionFormat(reportPartContent, container, element, preloadPromises).then(function (data) {
      data.formatData.id &&
        element.properties.setFormat({
          formatId: data.formatData.customId,
          format: data.formatData.format,
          groupBy: data.formatData.groupBy,
          formatDataType: data.formatData.formatDataType
        });
      return setFunctionElement(reportPartContent, element, data.functionData, true);
    });
  }

  /**
   * set function for element without function
   * @method convertNonFunctionElementsToFunction
   * @param  {object}                             reportPartContent [description]
   * @param  {string}                             newFunctioName    !optional if don't provide use default function
   * @return {Promise}                                              [description]
   */
  function convertNonFunctionElementsToFunction(reportPartContent, newFunctioName) {
    return flow(
      flatMap(function (container) {
        return flow(
          filter({ haveFunction: false }),
          map(function (element) {
            if (newFunctioName) {
              var defaultFormatName = get(
                [element.fieldDataType, newFunctioName].join('.'),
                DEFAULT_FORMAT_BY_FUNCTION_DATA_TYPE
              );
              var defaultFormatPromise = Promise.resolve({});

              if (defaultFormatName) {
                defaultFormatPromise = getFormatData(element.fieldDataType, defaultFormatName);
              }
              return Promise.join(
                defaultFormatPromise,
                getFunctionData(FIELD_FUNCTION_MODE.FieldProperty, element.fieldDataType, newFunctioName),
                m.action(function (formatData, functionData) {
                  formatData.id &&
                    element.properties.setFormat({
                      formatId: formatData.id,
                      format: formatData.format,
                      groupBy: formatData.groupBy,
                      formatDataType: formatData.formatDataType
                    });
                  return setFunctionElement(reportPartContent, element, functionData);
                })
              );
            }
            return setDefaultFunctionElement(reportPartContent, container, element);
          })
        )(container.elements.peek());
      }),
      Promise.all
    )(reportPartContent.containerCollection);
  }

  // common check for function with have config object
  function checkCompatibleFunctionCommon(functionInfo, element, config) {
    var result = true;
    var hasAggregatedFunction = element.field.hasAggregatedFunction;
    var functionName = getOr(ReportPartFieldConstants.EMPTY_FUNCTION.name, 'name', functionInfo);

    if (functionInfo.userDefined) {
      // if function is userDefined treat it like group function
      functionName = FUNCTION.GROUP.NAME;
    }

    if (config.SUPPORTED) {
      var supportedList = config.SUPPORTED[element.fieldDataType] || config.SUPPORTED.DEFAULT;
      var supportedFunction = map('NAME', supportedList);

      hasAggregatedFunction &&
        (supportedFunction = concat([ReportPartFieldConstants.EMPTY_FUNCTION.name], supportedFunction));
      result = includes(functionName, supportedFunction);
    }
    if (config.UNSUPPORTED) {
      var unsupportedList = config.UNSUPPORTED[element.fieldDataType] || config.UNSUPPORTED.DEFAULT;
      var unsupportedFunction = map('NAME', unsupportedList);

      hasAggregatedFunction &&
        (unsupportedFunction = without([ReportPartFieldConstants.EMPTY_FUNCTION.name], unsupportedFunction));
      result = result && !includes(functionName, unsupportedFunction);
    }
    return result;
  }

  /**
   * @method checkedCompatibleFunctionHasAggregatedFunction
   * @param  {object}                                       functionInfo [description]
   * @return {Boolean}                                                    [description]
   */
  function checkedCompatibleFunctionHasAggregatedFunction(functionInfo) {
    return !functionInfo.name || functionInfo.userDefined;
  }

  /**
   * Apply data format to value
   * @param  {any} fieldValue
   * fieldValue {
        originalValue:
        value:
      }
   * @param  {object} fieldConfig
   * @param  {array} dataSource
   * @param  {array} isApplyAlternativeText
   * @return {string}
   */
  function getDataFormat(fieldValue: any, fieldConfig: Object, dataSource: any, isApplyAlternativeText: Boolean) {
    const { value, originalValue } = isObject(fieldValue) ? fieldValue : { value: fieldValue, originalValue: null };

    if (isNil(fieldConfig)) {
      return value;
    }

    // apply alternative text by default
    isApplyAlternativeText = !isBoolean(isApplyAlternativeText) || isApplyAlternativeText;
    if (fieldConfig.fieldColumnType == 'side total')
      isApplyAlternativeText = false;
    const valueForAlternativeText = !isNil(originalValue) ? originalValue : value;
    const alternativeText =
      isApplyAlternativeText && getAlternativeText(valueForAlternativeText, fieldConfig, dataSource);

    return alternativeText || fieldConfig.conditionalFormattingAlternativeText || applyFormater(fieldConfig.fieldDataType, fieldConfig.fieldFormatData, value, dataSource);
  }

  function applyFormater(dataType, fieldFormat, value, dataSource) {
    if (dataType === DATA_TYPES.TIME) {
      fieldFormat = fieldFormat || TIME_FORMAT;
    }

    if (!fieldFormat) {
      return value;
    }

    var fnFormatter;
    var inputFormat;

    switch (dataType) {
      case DATA_TYPES.NUMERIC:
      case DATA_TYPES.MONEY:
        fnFormatter = convertToNumeralFormat;
        break;
      case DATA_TYPES.TIME:
      case DATA_TYPES.DATE_TIME:
        inputFormat = get('element.reportPartElm.dateTimeFormatData.dataFormat', dataSource);
        var dateConfig = DATE_TIME_FORMAT[fieldFormat];

        if (
          dateConfig &&
          dateConfig.FOR_DATE_SETTING &&
          !includes(uiState.currentUser.dateFormat, dateConfig.FOR_DATE_SETTING)
        ) {
          var groupBy = dateConfig.GROUP_BY;

          fieldFormat = DATE_TIME_GROUP_BY[groupBy].DEFAULT_FORMAT[uiState.currentUser.dateFormat];
        }
        fnFormatter = convertToDateTimeFormat;
        break;
      default:
      // do nothing
    }

    return fnFormatter ? fnFormatter(fieldFormat, value, null, inputFormat) : value;
  }

  function valueInRange(checkValue, item) {
    return inRange(toNumber(item.from), toNumber(item.to), checkValue) || checkValue === toNumber(item.to);
  }

  /**
   * Get configuration data for alternative text
   * @method getConfigDataByRange
   * @param {*} value
   * @param {*} configData
   * @param {*} dataSource
   */
  function getConfigDataByRange(value, configData, dataSource) {
    if (!configData) {
      return null;
    }

    var fieldElement = get('element', dataSource);
    var definedText = [];
    var dateObject = null;

    // Case for datetime alternative text
    if (isNil(fieldElement)) {
      fieldElement = dataSource;
    }
    if (get('reportPartElm.previewDataType', fieldElement) === DATA_TYPES.TIME) {
      value = convertToDateTimeFormat(TIME_FORMAT, value, false);
    }

    if (configData.value && configData.value.length) {
      // config by value
      dateObject = checkDateValue();
      definedText = configData.value.filter(function (item) {
        if (dateObject) {
          // compare between string and int
          return dateObject.value == item.key;
        } else if (!isNil(item.text) && value.toString().toUpperCase() === item.key.toString().toUpperCase()) {
          // verify that alt text properties are defined and match the axis label value
          // dateObject will be undefined when applying alternative text for date 'YYYY' field
          // Check for just the case when dateObject is undefined and when alt text is defined
          if (isNil(dateObject)) {
            return item;
          }
        } else if(value == "null" && item.key == UIConstants.NULL_VALUE) {
          return item
        }
        else if(value == "" && item.key == UIConstants.BLANK_VALUE) {
          return item
        }
        return value.toString().toUpperCase() === item.key.toString().toUpperCase();
      });
    } else if (configData.rangeValue && configData.rangeValue.length) {
      // config by range value
      dateObject = checkDateValue();
      definedText = configData.rangeValue.filter(function (item) {
        if (dateObject) {
          if (item.from <= item.to) {
            return item.from <= dateObject.value && dateObject.value <= item.to;
          }
          return item.from <= dateObject.value || dateObject.value <= item.to;
        }
        return valueInRange(toNumber(value), item);
      });
    } else if (configData.rangePercent && configData.rangePercent.length && has('record', dataSource)) {
      // config by range percent
      var valueByPercent = get(
        COLUMN_NAME_PREFIX.PERCENTAGE_VALUE + get('columnName', fieldElement),
        dataSource.record
      );

      definedText =
        !isNil(valueByPercent) &&
        configData.rangePercent.filter(function (item) {
          return valueInRange(toNumber(valueByPercent), item);
        });
    } else if (configData.allValues && configData.allValues.length) {
      definedText = configData.allValues;
    }

    return get('text', head(definedText));

    function checkDateValue() {
      var dateTimeFormatData = get('reportPartElm.dateTimeFormatData', fieldElement);

      if (dateTimeFormatData) {
        var dateObj =
          // special case for day, we don't want to moment js to  use current month,
          // because current month may have only 28 day for feb
          // forcing all case to make sure it will convert to same month if return date object for chart
          // Added case for value integer checking since moment `.date()` only accepts integers
          dateTimeFormatData.dataFormat === 'D' && Number.isInteger(value)
            ? moment()
              .startOf('year')
              .date(value)
            : moment(value, dateTimeFormatData.dataFormat, true);
        if (dateObj.isValid()) {
          var formattedValue = dateObj.format(dateTimeFormatData.compareFormat);
          // Day of week on FE use start of day 0, BE use 1
          if (dateTimeFormatData.compareFormat === 'E') {
            var dow = parseInt(value);
            if (!isNaN(dow) && (dow >= 1 && dow <= 7)) {
              formattedValue = nth(value, TimeFormatConstants.DAY_OF_WEEK_VALUES);
            }
          }
          return { value: formattedValue, format: dateTimeFormatData.compareFormat };
        }
      }
      return null;
    }
  }

  /**
   * Get alternative text
   * @param  {string} value           value need change to alternative text
   * @param  {object} alternativeText alternative text settings
   * @param  {array} dataSource       list field source
   */
  function getAlternativeText(value, fieldConfig, dataSource) {
    if (!fieldConfig || !fieldConfig.alternativeText) {
      return null;
    }

    // special case for null values only for Text fields to treat null as "null"
    if (isNil(value) && fieldConfig.fieldDataType === DATA_TYPES.TEXT) {
      const convertNullToEmpty = AppGlobal.advancedSettings.other.convertNullToEmptyStringValue;
      value = String.commonStringValue(value, convertNullToEmpty);
    }

    return getConfigDataByRange.call(null, value, fieldConfig.alternativeText, dataSource);
  }

  function openHyperlinkAction(props, fieldConfig, record, CommonActionPopup) {
    var subReport = get('subReport', fieldConfig);
    var customURL = get('customURL', fieldConfig);
    var embeddedJavascript = get('embeddedJavascript', fieldConfig);
    var numberOfPointActions = get('numberOfPointActions', fieldConfig);

    if (numberOfPointActions === 1) {
      get('selectedReport', subReport) && routeUtils.openSubReport(props, subReport, record, fieldConfig);
      get('url', customURL) && openCustomURL(props, customURL, record, fieldConfig);
      get('script', embeddedJavascript) && performEmbeddedJavascript(props, embeddedJavascript, record, fieldConfig);
    } else if (numberOfPointActions > 1) {
      props.openPopup(CommonActionPopup, { fieldConfig: fieldConfig }).then(function (action) {
        action === 'openSubReport' && routeUtils.openSubReport(props, subReport, record, fieldConfig);
        action === 'openCustomURL' && openCustomURL(props, customURL, record, fieldConfig);
        action === 'performEmbeddedJavascript' &&
          performEmbeddedJavascript(props, embeddedJavascript, record, fieldConfig);
      });
    }
  }

  function openOtherItemsAction(props, dataSource, isClickable) {
    return props.openPopup(DrillPopup, { dataSource, isClickable }).then(record => ({ record }));
  }

  function openCustomURL(props, customURL, record, fieldConfig) {
    var reportPart = props.reportPart;
    var URL = customURL.url;
    var previewData = m.toJS(reportPart.previewData);
    var previewFieldsMapping = previewData.fieldsMapping;

    var httpReg = /^https?:\/\//i;
    var isExternal = httpReg.test(URL);

    return validateURL(reportPart, record, URL, isExternal, fieldConfig).then(function (msg) {
      if (!isEmpty(msg)) {
        return props.confirmMessage('error', msg.join(', '));
      }

      var mappingField;

      if (isExternal) {
        mappingField = getFilterMappingValuesWithExternalURL(record, previewFieldsMapping, URL, fieldConfig);
        routeUtils.openCustomURL(props, customURL, mappingField.filters, null, isExternal, fieldConfig);
      } else {
        mappingField = getFilterMappingValuesWithInternalURL(record, previewFieldsMapping, URL);
        var catAndReportName = findCatAndReportNameInUrl(URL);

        fetcher
          .getReportIdByNameAndCateName(catAndReportName.cat, catAndReportName.re, uiState.tenantId)
          .then(function (result) {
            if (result.data) {
              var reportId = result.data;

              routeUtils.openCustomURL(props, customURL, mappingField.filters, reportId, isExternal, fieldConfig);
            }
          });
      }
    });
  }

  /**
   * Validate and execute embedded javascript
   * @param {any} props
   * @param {any} embeddedJavascript
   * @param {any} record
   * @param {any} fieldConfig
   */
  function performEmbeddedJavascript(props: any, embeddedJavascript: any, record: any, fieldConfig: any) {
    const {
      reportPart,
      reportPart: {
        previewData: { fieldsMapping: previewFieldsMapping }
      }
    } = props;
    let script = embeddedJavascript.script;
    const httpReg = /^https?:\/\//i;

    // find all Urls in script
    const parseURlInScript = [];
    const regex = /urldecode<.*>/gi;
    const httpRegex = /(?:"|')https?:\/\/\S+(?:"|')/gi;
    let matched;

    while ((matched = regex.exec(script))) {
      parseURlInScript.push({
        url: matched[0].substring('urldecode<'.length, matched[0].length - 1),
        index: matched.index,
        length: matched[0].length
      });
    }

    // Find url in script
    // Ex: window.open("http://www.google.com/?q={[ShipCountry]}")
    // matched = '"http://www.google.com/?q={[ShipCountry]}"'
    while ((matched = httpRegex.exec(script))) {
      matched[0] = matched[0].substring(1, matched[0].length - 1);
      parseURlInScript.push({
        url: matched[0],
        index: matched.index + 1,
        length: matched[0].length
      });
    }

    Promise.map(parseURlInScript, ({ url }) => validateURL(reportPart, record, url, httpReg.test(url), fieldConfig))
      .then(result => {
        const validateMessage = [];
        for (let i = 0; i < result.length; i++) {
          !isEmpty(result[i]) && validateMessage.push(result[i].join(', '));
        }

        // show error message if one of url in script has error
        if (!isEmpty(validateMessage[0])) {
          return props.confirmMessage('error', validateMessage[0]).then(() => {
            throw new Error();
          });
        }
      })
      .then(() => {
        // build urls with params replaced
        const getURLLinkPromise = [];

        parseURlInScript.forEach((item, index) => {
          const url = item.url;
          const isExternal = httpReg.test(url);
          let mappingField;

          if (isExternal) {
            mappingField = getFilterMappingValuesWithExternalURL(record, previewFieldsMapping, url, fieldConfig);
            item.url = routeUtils.getCustomURLExternalLink(url, mappingField.filters);
          } else {
            mappingField = getFilterMappingValuesWithInternalURL(record, previewFieldsMapping, url);
            const catAndReportName = findCatAndReportNameInUrl(url);

            getURLLinkPromise.push(
              fetcher
                .getReportIdByNameAndCateName(catAndReportName.cat, catAndReportName.re, uiState.tenantId)
                .then(({ data: reportId }) => {
                  if (reportId) {
                    return {
                      index: index,
                      url: routeUtils.getCustomURLInternalLink(mappingField.filters, reportId)
                    };
                  }
                })
            );
          }
        });

        return Promise.all(getURLLinkPromise).then(result => {
          for (let i = 0; i < result.length; i++) {
            !isEmpty(result[i]) && (parseURlInScript[result[i].index].url = result[i].url);
          }
        });
      })
      .then(() => {
        parseURlInScript.forEach(item => {
          script = String.replaceAt(script, item.index, item.length, item.url);
        });
        const mappingField = getFilterMappingValuesWithExternalURL(record, previewFieldsMapping, script, fieldConfig);
        const formattedScript = routeUtils.getCustomURLExternalLink(script, mappingField.filters, fieldConfig);
        try {
          // NOSONAR
          new Function(formattedScript)();
        } catch (error) {
          return props.confirmMessage('error', MESSAGE.M11);
        }
      })
      .catch(function () {
        // do nothing
      });
  }

  function addContainers(source, containerName) {
    source[containerName] = {
      elements: [],
      name: containerName,
      visibleElement() {
        return filter('isVisible', this.elements);
      },
      numElement() {
        return this.visibleElement.length;
      }
    };
  }

  /**
   * Add in custom containers
   * @param {Array<any>} customContainers
   * @param {string} containerName
   * @returns {Object}
   */
  function addCustomContainers(customContainers: Array<any>, containerName: string): any {
    const container = {
      elements: [],
      name: containerName,
      visibleElement() {
        return filter('isVisible', this.elements);
      },
      numElement() {
        return this.visibleElement.length;
      }
    };

    // Cannot use push() here. It makes mobx becomes unstable.
    customContainers.splice(-1, 0, container);

    return container;
  }

  function hasElement() {
    var isAllContainer = first(arguments);
    var dataContainerArr;

    if (isBoolean(isAllContainer)) {
      dataContainerArr = tail(arguments) || [];
    } else {
      isAllContainer = true;
      dataContainerArr = arguments;
    }

    const count = reduce(
      function (re, dataContainer) {
        dataContainer = isString(dataContainer) ? this[dataContainer] : dataContainer;

        var visibleElement = dataContainer.visibleElement || dataContainer.elements;
        var hasElement = visibleElement && visibleElement.length;

        return isAllContainer ? re && hasElement : re || hasElement;
      }.bind(this),
      isAllContainer,
      dataContainerArr
    );

    return count > 0;
  }

  function hasAtLeastElement() {
    return hasElement.apply(this, concat([false], arguments));
  }

  function getElements(container, activeSerieKey, containerName) {
    var elms = (container && container.visibleElement) || [];
    var checkContainer = containerName ? containerName === get('name', container) : true;

    if (checkContainer && activeSerieKey) {
      return elms.filter(function (elm) {
        return elm.fieldName === activeSerieKey;
      });
    }

    return elms;
  }

  function populateReportChartContainer(data, name, callback) {
    var source = {};

    addContainers(source, name);

    if (!data) {
      return source;
    }

    var pickData = omit('elements', data);
    var elements = data.elements;
    var elm;

    !pickData.name && (pickData.name = name);

    /* convert elements json object to become list of ReportPartElement*/
    var convertElements = map(function (item) {
      elm = new ReportPartElement().readData(item);
      callback && callback(pickData, elm, item);

      return elm;
    }, elements);

    source[name].elements = convertElements || [];

    m.extendObservable(pickData, source[name]);

    return pickData;
  }

  function populateReportChartCustomContainer(data, customContainers) {
    each(container => {
      let customContainer = find({ name: container.name }, customContainers);
      if (isNil(customContainer)) {
        customContainer = addCustomContainers(customContainers, name);
      }

      /* convert elements json object to become list of ReportPartElement*/
      const convertElements = map(item => new ReportPartElement().readData(item), container.elements);

      customContainer.elements = convertElements;
    }, data);
  }

  /**
   * Find custom container by given name
   * @param {string} customContainerName Custom container name.
   * @param {any[]} customContainers Custom containers list
   * @returns {any}
   */
  function findCustomContainer(customContainerName, customContainers) {
    return find({ name: customContainerName }, customContainers)
  }

  /**
   * Remove
   * @param fields
   * @param customContainerName
   * @param customContainers
   * @param configReportPart
   */
  function removeFieldsFromContainer(fields, customContainerName, customContainers, configReportPart) {
    const customContainer = findCustomContainer(customContainerName, customContainers);
    if (!customContainer)
      return;

    if (isArray(fields)) {
      // if fields were given: remove only them
      const removeThresholdElementFromContainer = (container, fieldNameAlias) => {
        const elementToRemove = find(x => x.fieldNameAlias === fieldNameAlias, container.elements);
        elementToRemove && configReportPart.removeFieldFromContainer(container, elementToRemove);
      };
      forEach(f => removeThresholdElementFromContainer(customContainer, f.fieldName), fields);
    } else {
      // otherwise remove all
      const elementsToRemove = customContainer.elements.slice();
      forEach(e => {
        configReportPart.removeFieldFromContainer(customContainer, e);
      }, elementsToRemove);
    }
  }

  function buildMappingFields(reportPartType, subReport, filters) {
    switch (reportPartType) {
      case 0: // chart
      case 2: // gauge
      case 4: // map
        return buildMappingFieldsForChart(subReport, filters);
      default:
        return filters;
    }
  }

  function buildMappingFieldsForChart(subReport, filters) {
    var mappingFields = getOr([], 'mappingFields', subReport);
    var mappingField;

    each(function (filterItem) {
      mappingField = findMappingField(filterItem.key, mappingFields);

      filterItem.key = mappingField ? mappingField.fieldNameInSubReport : null;
    }, filters);

    return remove(isNil, filters);

    function findMappingField(fieldSourceName, mappingFields) {
      return find(function (mappingField) {
        return fieldSourceName === mappingField.fieldNameAlias;
      }, mappingFields);
    }
  }

  function validateURL(reportPart, foundRecords, url, isExternal, fieldConfig) {
    var fieldCollection = reportPart.fieldCollection;
    var previewData = m.toJS(reportPart.previewData);
    var previewFieldsMapping = previewData.fieldsMapping;

    var errorMessage = [];
    var mappingValues;
    var currentReportColumns = findColumnInURL(url);
    var fieldNameNotFound = [];

    currentReportColumns.forEach(function (column) {
      findIndex({ fieldName: column }, fieldCollection) === -1 && fieldNameNotFound.push(column);
    });
    if (!isEmpty(fieldNameNotFound)) {
      errorMessage.push(String.format(MESSAGE.M12, fieldNameNotFound.join(', ')));
      return Promise.resolve(errorMessage);
    }

    // validate reportName
    // does column exists
    // Check whether it has many data values
    if (isExternal) {
      mappingValues = getFilterMappingValuesWithExternalURL(foundRecords, previewFieldsMapping, url, fieldConfig);
      !mappingValues.success && errorMessage.push(MESSAGE.L02);
    } else {
      mappingValues = getFilterMappingValuesWithInternalURL(foundRecords, previewFieldsMapping, url);
      !mappingValues.success && errorMessage.push(MESSAGE.L02);
      var names = findCatAndReportNameInUrl(url);
      var catName = names.cat;
      var reportName = names.re;

      return validateExistingReport(catName, reportName, uiState.tenantId)
        .then(function (result) {
          !result.data && errorMessage.push(MESSAGE.M11);
          return result.data;
        })
        .then(function (result) {
          if (result) {
            var destinationReportColumns = findColumnInURL(url);

            return validateExistingField(catName, reportName, destinationReportColumns, uiState.tenantId).then(function (
              result
            ) {
              !result.data && errorMessage.push(String.format(MESSAGE.M13, destinationReportColumns.join(',')));
              return errorMessage;
            });
          }
          return errorMessage;
        });
    }

    return Promise.resolve(errorMessage);

    function findColumnInURL(url) {
      // Find p value like {[ShipCountry]}
      const fieldNamesRegex = /{\[(.*?)\]}/g;
      let groups,
        columns = [];
      while ((groups = fieldNamesRegex.exec(url))) {
        columns.push(groups[1]);
      }

      return columns;
    }
  }

  function findCatAndReportNameInUrl(url) {
    var getCatAndReportName = flow(
      split('&'),
      head,
      split('?'),
      last,
      split('='),
      last
    );
    var cateAndReportName = getCatAndReportName(url);
    var cateName = head(cateAndReportName.split('/'));
    var reportName = last(cateAndReportName.split('/'));

    return { cat: cateName === ReportDesignerConstants.UNCATEGORIZED ? null : cateName, re: reportName };
  }

  function getFilterMappingValuesWithExternalURL(records, previewFieldsMapping, url, fieldConfig) {
    var getUrlMappingField = function (url) {
      var charIndex = 0;
      var startIndex = 0;
      var endIndex = 0;
      var length = getOr(0, 'length', url);
      var fieldText = '';
      var fieldMapping = [];

      if (isString(url)) {
        while (charIndex < length) {
          startIndex = indexOfFrom('{', charIndex, url);
          if (startIndex < 0) {
            break;
          }
          endIndex = indexOfFrom('}', startIndex, url);

          if (endIndex < 0) {
            break;
          }
          fieldText = url.substring(startIndex + 1, endIndex);
          if (fieldText === '0') {
            fieldMapping.push({ fieldNameAlias: fieldConfig.fieldElement.fieldNameAlias });
          } else if (startsWith('[', fieldText) && endsWith(']', fieldText)) {
            fieldMapping.push({
              fieldNameAlias: fieldText.substring(1, fieldText.length - 1)
            });
          }
          charIndex = endIndex + 1;
        }
      }
      return fieldMapping;
    };

    return buildFilterDataSubReport(records, getUrlMappingField(url), previewFieldsMapping);
  }

  function getFilterMappingValuesWithInternalURL(records, previewFieldsMapping, url) {
    var getUrlMappingField = flow(
      split('&'),
      tail,
      map(function (item) {
        var splitItem = item.split('=');

        if (splitItem.length === 2) {
          return {
            fieldNameInSubReport:
              splitItem[0].indexOf('{') > -1 ? splitItem[0].substring(1, splitItem[0].length - 1) : splitItem[0],
            fieldNameAlias: splitItem[1].substring(1, splitItem[1].length - 1)
          };
        }
      })
    );

    return buildFilterDataSubReport(records, getUrlMappingField(url), previewFieldsMapping);
  }

  function getContainerByField(containerCollection, field) {
    m.untracked(function () {
      if (!field) {
        return null;
      }
      return filter(function (container) {
        var found = filter(function (item) {
          return item === field;
        });

        return !!found;
      }, containerCollection);
    });
  }

  /**
   * Reset report part setting to default for threshold and xy plane on chart setting
   * @param  {Array} elements  list of elements field
   * @param  {string} key      'threshold or axis'
   */
  function resetDefaultSetting(elements, key) {
    flow(
      flatMap(function (element) {
        return element.properties.otherProps;
      }),
      forEach(function (otherProps) {
        otherProps[key] = undefined;
      })
    )(elements);
  }

  /**
   * Transform cross filter's filter into report part.
   * @param {Object} reportPart
   * @param {Array} filters
   */
  function transformFilterWithReportPart(reportPart: Object, filters: Array) {
    return map(item => {
      let key;
      const sourceAlias = get('field.reportPartElm.field.sourceAlias', item);
      const fieldName = get('field.reportPartElm.field.fieldName', item);
      const functionName = get('field.reportPartElm.properties.dataFormattings.functionInfo.name', item);
      const relationshipId = get('field.reportPartElm.field.relationshipId', item);
      const fieldId = get('field.reportPartElm.field.fieldId', item);
      let finalFieldId = null;

      if (isEmpty(fieldId)) {
        return item;
      }

      if (
        functionName === FUNCTION_GROUP_NAME ||
        isEmpty(get('field.reportPartElm.properties.dataFormattings.functionInfo.id', item))
      ) {
        if (isNil(relationshipId) || relationshipId === EMPTY_GUID) {
          key = null;
          finalFieldId = fieldId;
        } else {
          key = `[${sourceAlias}].[${fieldName}]`;
        }
      } else {
        key = flow(
          find(item => {
            const relationshipIdValue = get('field.relationshipId', item);
            return (
              get('field.fieldName', item) === fieldName &&
              get('field.sourceAlias', item) === sourceAlias &&
              (relationshipIdValue === relationshipId || isEmpty(relationshipIdValue)) &&
              get('properties.dataFormattings.functionInfo.name', item) === functionName
            );
          }),
          get('field.fieldNameAlias')
        )(get('fieldCollection', reportPart));
      }

      let transformedFitler = {
        isCrossFilter: true,
        key: toString(key),
        value: item.value,
        dataFormat: get('field.reportPartElm.properties.dataFormattings.format.formatId', item)
      };

      finalFieldId && (transformedFitler.fieldId = finalFieldId);
      return transformedFitler;
    }, filters);
  }

  /**
   * Get default sort from default report part config.
   * @param {Object} defaultConfig
   * @param {String} reportPartStyle
   * @param {String} containerName
   * @return {String} defaultSort
   */
  function getDefaultSortFromDefaultConfig(defaultConfig: Object, reportPartStyle: String, containerName: String) {
    return flow(
      get(reportPartStyle),
      get('DEFAULT_SORT'),
      get(containerName)
    )(defaultConfig);
  }

  /**
   * Get default sort
   * @param {String} reportPartType
   * @param {String} reportPartStyle
   * @param {String} containerName
   * @return {String} defaultSort
   */
  function getDefaultSort(reportPartType: String, reportPartStyle: String, containerName: String) {
    switch (reportPartType) {
      case ReportDesignerConstants.REPORT_PART_TYPE.Grid:
        return getDefaultSortFromDefaultConfig(GridDefaultConfig.DefaultConfig, reportPartStyle, containerName);
      case ReportDesignerConstants.REPORT_PART_TYPE.Chart:
      case ReportDesignerConstants.REPORT_PART_TYPE.Gauge:
      case ReportDesignerConstants.REPORT_PART_TYPE.Map:
        return getDefaultSortFromDefaultConfig(ChartDefaultConfig.DefaultConfig, reportPartStyle, containerName);
      case ReportDesignerConstants.REPORT_PART_TYPE.Form:
        return getDefaultSortFromDefaultConfig(FormDefaultConfig.DefaultConfig, 'Form', containerName);
      default:
        return 'ASC';
    }
  }

  /**
   * Render lazy loading bar.
   */
  function renderLazyLoadingBar() {
    return <LazyLoadingBar className='u_fullSize u_flex u_flexCenterAlignItems' />;
  }

  /**
   * Check to render lazy loading bar
   * @param {Object} reportPart
   * @param {Boolean} printingMode
   */
  function needToRenderLazyLoadingBar(reportPart: Object, printingMode: Boolean) {
    if (printingMode) {
      return isNil(reportPart);
    } else {
      const isLoadingData = get('isLoadingData', reportPart);
      return isLoadingData === undefined || isLoadingData === true;
    }
  }

  /**
   * Filter format list to display
   * @param {*} reportPartContent
   * @param {*} formats
   * @param {*} isRunningField
   * @param {*} containerName
   */
  function filterFormatForField(
    reportPartContent: any,
    formats: Array<any>,
    isRunningField: boolean,
    containerName: string
  ) {
    const partType = get('type', reportPartContent);
    const isPivotGrid =
      partType === REPORT_PART_TYPE.Grid &&
      get('properties.generalInfo.gridStyle', reportPartContent) === GRID_STYLES.PIVOT.value;
    const isFieldInValuesContainerOfPivotGrid = isPivotGrid && containerName === GRID_CONTAINER_NAME.VALUES;

    const isFieldInColumnsContainerOfPivotGrid = isPivotGrid && containerName === GRID_CONTAINER_NAME.COLUMNS;

    const isInSeparatorContainerOfGrid =
      partType === REPORT_PART_TYPE.Grid && containerName === GRID_CONTAINER_NAME.SEPARATORS;

    return remove(
      (
        format // Remove format for only part types
      ) =>
        (includes(partType, FORMATS_NOT_AVAILABLE_FOR_PART_TYPES.partType) &&
          includes(format.name, FORMATS_NOT_AVAILABLE_FOR_PART_TYPES.formats)) || // Remove format not on running field
        (isRunningField && includes(format.name, FORMATS_NOT_AVAILABLE_FOR_RUNNING_FIELD)) || // Remove format only on pivot
        (!isFieldInValuesContainerOfPivotGrid && includes(format.name, FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER)) || // Remove format not available for fields can not set subtotal
        ((isInSeparatorContainerOfGrid || isFieldInColumnsContainerOfPivotGrid) &&
          includes(format.name, FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL)),
      formats
    );
  }

  /**
   * Determine linear type from configField
   * @param {any} configField
   * @param {any} defaultOptionsByType
   */
  function isLinear(configField: any, defaultOptionsByType: Object) {
    // Get type of xAxis
    const xAxisTypeCategory = get('properties.otherProps.axis.izCategory', configField);
    const fieldType = get('previewDataTypeByFunction', configField);
    const chartHaveXYPlaneSetting = defaultOptionsByType.hasOwnProperty('izXYplane');

    return (
      chartHaveXYPlaneSetting &&
      !xAxisTypeCategory &&
      includes(fieldType, ReportPartChartConstants.LIST_COUNTABLE_DATA_TYPE)
    );
  }

  /**
   * check if field in subtotal expressions
   * @param   {Object}  gridStyle             grid style
   * @param   {Object}  container             container
   * @param   {Object}  configField           current selected field
   * @param   {Object}  containerCollection   containerCollection info
   * @return  {Boolean}                       [can field allow unsort]
   */
  function checkCanUnsort(gridStyle, container, configField, containerCollection) {
    var containerName = getOr('', 'name', container);

    switch (gridStyle) {
      case ReportPartGridConstants.GRID_STYLES.VERTICAL.value: // VERTICAL
      case ReportPartGridConstants.GRID_STYLES.HORIZONTAL.value: // HORIZONTAL
        if (containerName === ReportPartGridConstants.CONTAINER_NAME.SEPARATORS) {
          return false;
        }
        if (
          !isUndefined(configField) &&
          isFieldInSubtotalExpression(configField, containerCollection) &&
          getTotalField(containerCollection) > 32
        ) {
          return false;
        }
        return true;
      case ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value: // DRILL_DOWN
      case ReportPartGridConstants.GRID_STYLES.PIVOT.value: // PIVOT
        return false;
    }

    return false;
  }

  /**
   * Get total fields of grid
   * @param {Array} containerCollection
   */
  function getTotalField(containerCollection) {
    return flow(
      map(x => x.elements),
      flatMap(observableToArray)
    )(containerCollection).length;
  }

  /**
   * check if field in subtotal expressions
   * @param   {Object}  configField           current selected field
   * @param   {Object}  containerCollection   containerCollection info
   * @return  {Boolean}
   */
  function isFieldInSubtotalExpression(configField, containerCollection) {
    if (configField.hasSubTotal && !isEmpty(configField.properties.dataFormattings.subTotal.function)) {
      return true;
    }
    return flow(
      map(x => x.elements),
      flatMap(observableToArray),
      filter(x => !isUndefined(x) && x.hasSubTotal),
      some(
        // && !isEmpty(x.properties.dataFormattings.function)),
        x => includes('[' + configField.fieldName.toLowerCase() + ']',
          getOr('', 'properties.dataFormattings.subTotal.expression', x).toLowerCase())
      )
    )(containerCollection);
  }

  /**
   * Return an object contain formated value and original value
   * @param {String} columnName field mapping name
   * @param {Object} record contain record returned form api
   */
  function getFieldValue(columnName: String, record: Object): Object {
    const originalFieldName = ReportPartFieldConstants.CUSTOM_FORMAT_PREFIX + columnName;
    const value = get(columnName, record);
    const originalValue = getOr(value, originalFieldName, record);
    return {
      value,
      originalValue
    };
  }

  /**
   * get original value of a field
   * @param {Object} field
   * @param {Object} record
   * @returns {any}
   */
  function getOriginalValue(field: any, record: any) {
    var columnName = get('columnName', field);
    if (isNil(columnName)) {
      return undefined;
    }

    return !isNil(field) && reportPartElementUtils.checkUsingCustomFormat(field)
      ? get(ReportPartFieldConstants.CUSTOM_FORMAT_PREFIX + columnName, record)
      : get(columnName, record);
  }

  /**
   * convert array of observable to array
   * @param   {ObservableArray}  observable   current selected field
   * @return  {Array}                         [container with elements typeof ReportPartElement]
   */
  const observableToArray = curry(observable => {
    var arrResult = [];
    observable.forEach(item => arrResult.push(item));
    return arrResult;
  });

  /*
   *	Check filter rules before save
   * @param {Array<Object>} reportFilters
   * @returns {Object}
   */
  const checkFilterRule = (reportFilters: Array<any>): any => {
    const { emptyRequiredParamFilters, emptyNotVisibleRequiredFilter, emptyVisibleRequiredFilter } = reportFilters;
    // For filter made from input param
    if (!isEmpty(emptyRequiredParamFilters)) {
      if (!isEmpty(emptyNotVisibleRequiredFilter)) {
        return MESSAGES.EMPTY_REQUIRED_PARAM_FILTER(emptyRequiredParamFilters, emptyNotVisibleRequiredFilter);
      }
      return MESSAGES.EMPTY_FILTER_PARAM(emptyRequiredParamFilters);
    }

    // For normal filter
    if (!isEmpty(emptyNotVisibleRequiredFilter)) {
      return MESSAGES.EMPTY_REQUIRED_FILTER(emptyNotVisibleRequiredFilter);
    }
    if (!isEmpty(emptyVisibleRequiredFilter)) {
      return MESSAGES.EMPTY_VISIBLE_REQUIRED_FILTER(emptyVisibleRequiredFilter);
    }

    return null;
  };

  /**
   * Get data utils
   */
  const getDataUtils = (fieldStore, editMode, loader, confirmFn, updateReportParts) => {
    const { reportStore, reportFilters } = fieldStore;

    // Change filters' requiredFilterNotApply to false when Apply filter button is pressed
    reportFilters.filterFields.forEach(filter => (filter.requiredFilterNotApply = false));
    const validateFilterMessage = checkFilterRule(reportFilters);

    if (!validateFilterMessage) {
      // Update config field list for report filter info
      editMode && reportFilters.updateConfigFieldList();
      loader.startLoading();
      return reportStore
        .detectSchemaChange()
        .then((result: any): any =>
          result.hasChanged
            ? confirmFn('Information', {
              description: MESSAGES.UPDATE_RESULT(result.filterFields),
              type: 'info',
              isHTMLFormat: true,
              showCloseIndicator: true
            }).then(reportStore.updateResults.bind(reportStore))
            : updateReportParts()
        )
        .finally((): boolean => {
          reportFilters.resetUpdateResult();
          loader.endLoading();
          return true;
        });
    } else {
      return confirmFn('Information', {
        description: validateFilterMessage,
        type: 'info',
        isHTMLFormat: true,
        showCloseIndicator: true
      }).then((): boolean => false);
    }
  };

  /**
   * Change 'dynamic_subtotal' thresholds function settings. This utility method is needed when
   * field changes its own function.
   * @param configField field
   * @param newFunctionId new function id
   * @param newFunctionName new function name
   * @param reportPartContent current report part content
   */
  const updateThresholdFunction = (configField, newFunctionId, newFunctionName, reportPartContent) => {
    // Update data formattings for element
    const dynamicGlobalContainer = find({
      name: MapConstants.ContainerName.THRESHOLDS_SUBTOTAL
    }, reportPartContent.customContainers);

    if (!dynamicGlobalContainer)
      return;

    forEach(el => {
      const dataFormattings = get('properties.dataFormattings', el);
      dataFormattings.function = newFunctionId;
      dataFormattings.functionInfo.name = newFunctionName;
      dataFormattings.functionInfo.id = newFunctionId;
    }, dynamicGlobalContainer.elements);

    // Update cell color settings for current config field
    const cellColorSettings = getOr(null, 'properties.dataFormattings.color.cellColor', configField);
    if (cellColorSettings) {
      // get non empty cellColor['xxx']
      const metrics = flow(
        values,
        compact
      )(cellColorSettings);
      // 'Country', 'City',... metrics
      forEach(metric => {
        if (!metric.value)
          return;
        // 'High', 'Low' thresholds
        forEach(itemData => {
          if (itemData
            && itemData.thresholdType === ThresholdConstants.THRESHOLD.THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value) {
            // Threshold fields
            forEach(itemDataField => {
              itemDataField.functionId = newFunctionId;
              itemDataField.functionName = newFunctionName;
            }, itemData.fields);
          }
        }, metric.value);
      }, metrics);
    }
  };

  const applyConditionalFormattingBase = (reportPart) => {
    const { CONDITION_TYPE, FORMAT_TYPE } = ConditionalFormattingConstants;

    const previewDataFieldsMapping = reportPart.previewDataFieldsMapping;
    const properties = reportPart.reportPartContent.properties;
    const conditionalFormattingRules = getOr([], 'view.conditionalFormatting.rules', properties);

    const allRules = conditionalFormattingRules.map((rule, index) => {
      const clonedRule = clone(rule);
      clonedRule.position = index;
      return clonedRule;
    });

    const getColumnName = (fieldName) => {
      const fieldMappingData = find({ fieldNameAlias: fieldName }, previewDataFieldsMapping);
      return getOr(null, 'columnName', fieldMappingData);
    }

    const checkDateTimeValue = (dateTimeFormatData, recordColumnValue) => {
      if (dateTimeFormatData) {
        var dateObj = dateTimeFormatData.dataFormat === 'D' && Number.isInteger(recordColumnValue)
          ? moment().startOf('year').date(recordColumnValue)
          : moment(recordColumnValue, dateTimeFormatData.dataFormat, true);
        if (dateObj.isValid()) {
          var formattedValue = dateObj.format(dateTimeFormatData.compareFormat);
          // Day of week on FE use start of day 0, BE use 1
          if (dateTimeFormatData.compareFormat === 'E') {
            var dow = parseInt(recordColumnValue);
            if (!isNaN(dow) && (dow >= 1 && dow <= 7)) {
              formattedValue = nth(recordColumnValue, TimeFormatConstants.DAY_OF_WEEK_VALUES);
            }
          }
          return formattedValue;
        }
      }
      return null;
    }

    const checkCondition = (record, condition) => {
      const conditionFieldName = get('conditionField.name', condition);
      const conditionType = get('type', condition);
      const conditionValue = get('value', condition);
      const dateTimeFormatData = get('conditionField.dateTimeFormatData', condition);
      const previewDataType = get('conditionField.previewDataType', condition);

      const recordColumnName = getColumnName(conditionFieldName);
      let recordColumnValue = record[recordColumnName];
      if (previewDataType === DATA_TYPES.TIME) {
        recordColumnValue = convertToDateTimeFormat(TIME_FORMAT, recordColumnValue, false);
      }
      const recordDateTimeColumnValue = checkDateTimeValue(dateTimeFormatData, recordColumnValue);

      switch (conditionType) {
        case CONDITION_TYPE.ALL_VALUES.value:
          return true;
        case CONDITION_TYPE.VALUE.value:
          let value = recordDateTimeColumnValue || recordColumnValue || '';
          value = isNumber(conditionValue) ? toNumber(value) : value;
          return conditionValue.toString().toUpperCase() === value.toString().toUpperCase();
        case CONDITION_TYPE.RANGE_VALUE.value:
          if (recordDateTimeColumnValue) {
            const { from, to } = conditionValue;
            return recordDateTimeColumnValue >= from && recordDateTimeColumnValue <= to;
          }
          return valueInRange(toNumber(recordColumnValue), conditionValue);
        case CONDITION_TYPE.RANGE_PERCENT.value:
          const recordColumnPercentValue = get(COLUMN_NAME_PREFIX.PERCENTAGE_VALUE + recordColumnName, record);
          return valueInRange(toNumber(recordColumnPercentValue), conditionValue);
      }
    }

    const setFormat = (rules) => {
      const conditionalFormatting = [];

      const getCellConditionalFormatting = (fieldName, applyToSideTotal, additionalConditions) => {
        const cellDefinition = {
          columnName: getColumnName(fieldName),
          applyToSideTotal: applyToSideTotal,
          additionalConditions: additionalConditions
        };

        let existingCellConditionalFormatting = null;
        if (additionalConditions.length === 0) {
          existingCellConditionalFormatting = find((cellConditionalFormatting) => {
            const existingCellDefinition = cellConditionalFormatting.definition;
            return existingCellDefinition.additionalConditions.length === 0 &&
              existingCellDefinition.columnName === cellDefinition.columnName;
          }, conditionalFormatting);
        }

        if (existingCellConditionalFormatting)
          return existingCellConditionalFormatting;

        const newCellConditionalFormatting = {
          definition: cellDefinition,
          value: {}
        };
        conditionalFormatting.push(newCellConditionalFormatting);
        return newCellConditionalFormatting;
      }

      forEach(rule => {
        const formatType = get('format.type.value', rule);
        const formatValue = get('format.value', rule);
        const priority = rule.position;

        forEach(field => {
          const fieldName = getOr(field, 'name', field);
          const applyToSideTotal = getOr(false, 'applyToSideTotal', field);
          const additionalConditions = getOr([], 'additionalConditions', field);
          const cellConditionalFormatting = getCellConditionalFormatting(fieldName, applyToSideTotal, additionalConditions);

          switch (formatType) {
            case FORMAT_TYPE.ALTERNATIVE_TEXT.value:
              cellConditionalFormatting.value.altText = { value: formatValue, priority: priority };
              break;
            case FORMAT_TYPE.BACKGROUND_COLOR.value:
              cellConditionalFormatting.value.bgColor = { value: formatValue, priority: priority };
              break;
            case FORMAT_TYPE.FONT_COLOR.value:
              cellConditionalFormatting.value.fontColor = { value: formatValue, priority: priority };
              break;
            case FORMAT_TYPE.FONT_FORMAT.value:
              cellConditionalFormatting.value.fontFamily = { value: formatValue.font.fontFamily, priority: priority };
              cellConditionalFormatting.value.fontSize = { value: formatValue.font.fontSize, priority: priority };
              cellConditionalFormatting.value.italic = { value: formatValue.style.fontItalic, priority: priority };
              cellConditionalFormatting.value.bold = { value: formatValue.style.fontBold, priority: priority };
              cellConditionalFormatting.value.underline = { value: formatValue.style.fontUnderline, priority: priority };
              break;
          }
        }, rule.fields);

      }, rules);

      return conditionalFormatting;
    }

    const checkAdditionalConditions = (rowDataItem, additionalConditions) => {
      for (let i = 0; i < additionalConditions.length; i++) {
        if (!checkCondition(rowDataItem, additionalConditions[i]))
          return false;
      }
      return true;
    }

    return {
      allRules: allRules,
      getColumnName: getColumnName,
      checkCondition: checkCondition,
      setFormat: setFormat,
      checkAdditionalConditions: checkAdditionalConditions
    };
  }

  const applyConditionalFormattingForPivotAndDrilldownGrids = (rows, reportPart) => {
    const { allRules, checkCondition, setFormat, checkAdditionalConditions } = applyConditionalFormattingBase(reportPart);

    const getRulesForRow = (rowData) => {
      return allRules.filter((rule) => {
        const condition = rule.condition;
        const additionalConditions = getOr([], 'additionalConditions', condition);

        for (let i = 0; i < rowData.length; i++) {
          const rowDataItem = rowData[i];
          if (!checkAdditionalConditions(rowDataItem, additionalConditions))
            continue;
          if (checkCondition(rowDataItem, condition))
            return true;
        }
      });
    }

    rows.forEach((row) => {
      const rowData = getOr([], 'currentRow.rowDataObject.data', row);
      const rules = getRulesForRow(rowData);
      const conditionalFormatting = setFormat(rules);

      row.currentRow.rowDataObject.conditionalFormatting = conditionalFormatting;
    });
  }

  const applyConditionalFormattingForHorizontalAndVerticalGrids = (records, reportPart) => {
    const { allRules, checkCondition, setFormat } = applyConditionalFormattingBase(reportPart);
    forEach(record => {
      const rules = filter(rule => checkCondition(record, rule.condition), allRules);
      const conditionalFormatting = setFormat(rules);
      record.conditionalFormatting = conditionalFormatting;
    }, records);
  }

  const getCellConditionalFormattingValue = (columnName, conditionalFormatting, record, reportPart, isSideTotalCell) => {
    let checkAdditionalConditions = () => true;
    if (reportPart) {
      checkAdditionalConditions = applyConditionalFormattingBase(reportPart).checkAdditionalConditions;
    }

    const cellConditionalFormattingValue = {};
    const overrideCellConditionalFormattingValue = (conditionalFormattingItemValue) => {
      const properties = Object.keys(conditionalFormattingItemValue);
      properties.forEach(property => {
        const existingProperty = cellConditionalFormattingValue[property];
        const newProperty = conditionalFormattingItemValue[property];
        if (!existingProperty || newProperty.priority > existingProperty.priority) {
          cellConditionalFormattingValue[property] = newProperty;
        }
      });
    }

    for (let i = 0; i < conditionalFormatting.length; i++) {
      const conditionalFormattingItem = conditionalFormatting[i];
      const definition = conditionalFormattingItem.definition;
      const definitionColumnName = definition.columnName;
      if (definitionColumnName !== columnName)
        continue;

      const applyToSideTotal = definition.applyToSideTotal;
      if (isSideTotalCell && !applyToSideTotal)
        continue;

      const additionalConditions = definition.additionalConditions;
      if (checkAdditionalConditions(record, additionalConditions)) {
        overrideCellConditionalFormattingValue(conditionalFormattingItem.value);
      }
    }

    return cellConditionalFormattingValue;
  };

  const getConditionalFormattingForColapsedCell = (rowDataObject) => {
    const key = rowDataObject.key;
    const styles = {};

    const getConditionalFormattingValue = (rowObject) => {
      const childs = rowObject.childs || [];
      if (childs.length === 0 || !rowObject.isExpand) {
        const conditionalFormatting = rowObject.conditionalFormatting;
        const cellCondititonalFormatting = find(conditionalFormattingItem => conditionalFormattingItem.definition.columnName === key, conditionalFormatting);
        if (cellCondititonalFormatting) {
          const cellCondititonalFormattingValue = cellCondititonalFormatting.value;
          const properties = Object.keys(cellCondititonalFormattingValue);
          properties.forEach(property => {
            const existingProperty = styles[property];
            const newProperty = cellCondititonalFormattingValue[property];
            if (!existingProperty || newProperty.priority > existingProperty.priority) {
              styles[property] = newProperty;
            }
          });
        }
      } else {
        childs.forEach(childRowObject => getConditionalFormattingValue(childRowObject));
      }
    };

    getConditionalFormattingValue(rowDataObject);

    return styles;
  };

  /**
   * Parse HTML string and get its text content
  */
  const getTextContentFromHtmlString = (htmlString) => {
    let textContent = '';
    $izenda.parseHTML(htmlString).forEach(el => textContent += el.textContent);
    return textContent;
  };

  /**
   * Check whether running field okay or not.
   * @param reportPart {object} Report part.
   * @param container {object} Field container.
   * @param isRunningField {boolean} isRunningField.
   */
  const isInvalidRunningField = (reportPart, container, isRunningField) => {
    if (!isRunningField) {
      return false;
    }
    const type = ReportDesignerConstants.REPORT_PART_TYPE;
    let containerType = {};
    switch (reportPart.type) {
      case type.Chart:
        containerType = { name: 'Chart', key: 'chartType' };
        break;
      case type.Gauge:
        containerType = { name: 'Gauge', key: 'reportPartStyle' };
        break;
      case type.Map:
        containerType = { name: 'Map', key: 'chartType' };
        break;
      case type.Grid:
        containerType = { name: 'Grid', key: 'gridStyle' };
        break;
    }
    const typeReport = reportPart.reportPartContent[containerType.key];
    return includes(
      container.name,
      ReportBodyConstants.RUNNING_FUNCTION_INVALID_CONTAINER[containerType.name][typeReport]
    );
  };

  module.exports = {
    getAlternativeText: getAlternativeText,
    getDefaultFunction: function (soure, type) {
      return get(type || '', soure) || get('Others', soure);
    },
    getFieldValue,
    getOriginalValue,
    /**
     * Get data format of value based on its config
     * @type {[type]}
     */
    getDataFormat: getDataFormat,
    applyFormater: applyFormater,
    getConfigDataByRange: getConfigDataByRange,
    getFormatInfo: getFormatData,
    getDefaultFormat: getDefaultFormat,
    openHyperlinkAction: openHyperlinkAction,
    openCustomURL: openCustomURL,
    performEmbeddedJavascript: performEmbeddedJavascript,
    getDefaultFunctionFormat: getDefaultFunctionFormat,
    convertNonFunctionElementsToFunction: convertNonFunctionElementsToFunction,
    setDefaultFunctionElement: setDefaultFunctionElement,
    setFunctionElement: setFunctionElement,
    addContainers: addContainers,
    addCustomContainers: addCustomContainers,
    hasElement: hasElement,
    checkedCompatibleFunctionHasAggregatedFunction: checkedCompatibleFunctionHasAggregatedFunction,
    checkCompatibleFunctionCommon: checkCompatibleFunctionCommon,
    hasAtLeastElement: hasAtLeastElement,
    getElements: getElements,
    getUniqueName: getUniqueName,
    buildFilterDataSubReport: buildFilterDataSubReport,
    populateReportChartContainer: populateReportChartContainer,
    populateReportChartCustomContainer: populateReportChartCustomContainer,
    buildMappingFields: buildMappingFields,
    findCustomContainer: findCustomContainer,
    removeFieldsFromContainer: removeFieldsFromContainer,
    validateURL: validateURL,
    buildDataSourceElement: buildDataSourceElement,
    findCatAndReportNameInUrl: findCatAndReportNameInUrl,
    getContainerByField: getContainerByField,
    resetDefaultSetting: resetDefaultSetting,
    transformFilterWithReportPart: transformFilterWithReportPart,
    getDefaultSort: getDefaultSort,
    renderLazyLoadingBar: renderLazyLoadingBar,
    needToRenderLazyLoadingBar: needToRenderLazyLoadingBar,
    filterFormatForField: filterFormatForField,
    isLinear: isLinear,
    checkCanUnsort: checkCanUnsort,
    getFunction: getFunction,
    openOtherItemsAction: openOtherItemsAction,
    getDataUtils: getDataUtils,
    checkFilterRule: checkFilterRule,
    updateThresholdFunction: updateThresholdFunction,
    applyConditionalFormattingForHorizontalAndVerticalGrids: applyConditionalFormattingForHorizontalAndVerticalGrids,
    applyConditionalFormattingForPivotAndDrilldownGrids: applyConditionalFormattingForPivotAndDrilldownGrids,
    getCellConditionalFormattingValue: getCellConditionalFormattingValue,
    getConditionalFormattingForColapsedCell: getConditionalFormattingForColapsedCell,
    getTextContentFromHtmlString: getTextContentFromHtmlString,
    isInvalidRunningField: isInvalidRunningField
  };
})();



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.ReportPartUtils.js